<template>
  <div class="q-pa-md column gap-4 full-height">
    <q-card class="q-pa-md column gap-3">
      <div class="row items-center justify-between">
        <div class="row items-center">
          <q-btn
            flat
            round
            dense
            icon="arrow_back"
            color="primary"
            class="q-mr-sm"
            @click="goBack"
          />
          <div class="text-h6">预瞄信息（HU cps_tool）</div>
        </div>
        <q-btn
          dense
          outline
          color="primary"
          label="全部重置"
          @click="resetAll"
        />
      </div>

      <q-banner dense class="bg-grey-2 text-grey-9 q-mb-sm">
        <div>
          信号与类型见
          <code>docs/previewinfo_soa.md</code>
          ；SOA 名与
          <code>VehicleProperty.java</code>
          中
          <code>SusPreviewInfo_MSG_PCM_*</code>
          一致。本地服务只做
          <code>adb -s localhost:5559 shell cps_tool set 信号名=…</code>
          （与
          <code>x11-media-test-drive</code>
          同源逻辑）：
          <strong>标量</strong>
          直接传数字；
          <strong>float[]</strong>
          由页面拼成带单引号的 JSON 字面量（如
          <code>'[1,2,3]'</code>
          ）写入命令行；
          <strong>uint8[]</strong>
          （BytesValue）由页面把 JSON 数组转为
          <code>b&quot;hex&quot;</code>
          并加外层单引号。回显时由页面根据
          <code>valueRaw</code>
          解析为数字、
          <code>[]</code>
          或
          <code>b&quot;…&quot;</code>
          字节序列。修改后自动写车（节流）。
        </div>
        <div class="text-caption text-grey-7 q-mt-xs">
          请运行
          <code>npm run hu:cps-tool-server</code>
          ；0106DB 后需先
          <code>cps_tool debug test_mode true</code>
          （见
          <code>docs/cps_tool使用文档.md</code>
          ）。
        </div>
      </q-banner>

      <div class="signal-list">
        <div
          v-for="item in previewSignals"
          :key="item.signalName"
          class="q-pa-sm q-mb-sm rounded-borders bg-grey-1"
        >
          <div class="row items-start justify-between q-mb-xs">
            <div class="col-8">
              <div class="text-body1">
                <span class="text-weight-medium">{{ item.zh }}</span>
                <span class="text-caption text-grey-7 q-ml-sm">
                  {{ item.displayName }}
                </span>
                <span class="text-caption text-grey-6 q-ml-xs block">
                  {{ item.signalName }}
                </span>
              </div>
              <div class="text-caption text-grey-7">
                类型 {{ item.soaType }} · 订阅信号（set 注入）
              </div>
              <div
                v-if="isArraySoaType(item.soaType)"
                class="text-caption text-warning q-mt-xs"
              >
                {{ cpsArrayFormatTip(item.soaType) }}
              </div>
            </div>
            <div class="col-4 text-right">
              <div class="text-caption text-grey-7">当前值</div>
              <div class="text-subtitle2 text-primary text-break">
                {{ formatCurrentDisplay(item.signalName, item.soaType) }}
              </div>
            </div>
          </div>

          <!-- 数组：JSON 文本 -->
          <div v-if="isArraySoaType(item.soaType)" class="column q-gutter-y-sm">
            <q-input
              v-model="arrayDraft[item.signalName]"
              type="textarea"
              outlined
              dense
              autogrow
              :label="`JSON 数组，长度 ${parseArrayLength(item.soaType)}`"
              :hint="arrayInputHint(item.soaType)"
              @blur="() => commitArrayAndMaybeSend(item)"
            />
            <div class="row q-gutter-sm">
              <q-btn
                dense
                outline
                color="primary"
                label="应用并下发"
                @click="commitArrayAndMaybeSend(item, { forceNotify: true })"
              />
              <q-btn
                dense
                flat
                color="secondary"
                label="从车端获取"
                @click="handleGet(item.signalName, item.soaType)"
              />
            </div>
          </div>

          <!-- 标量 -->
          <div v-else class="row items-center q-col-gutter-sm">
            <div class="col-4">
              <q-input
                dense
                outlined
                type="number"
                :model-value="scalarValue(item.signalName)"
                :label="`数值 (${scalarConfig(item.signalName, item.soaType).min} ~ ${scalarConfig(item.signalName, item.soaType).max})`"
                @update:model-value="
                  (val) =>
                    clampScalar(
                      item.signalName,
                      item.soaType,
                      val as number | null | undefined
                    )
                "
              />
            </div>
            <div class="col-8">
              <q-slider
                :model-value="scalarValue(item.signalName)"
                :min="scalarConfig(item.signalName, item.soaType).min"
                :max="scalarConfig(item.signalName, item.soaType).max"
                :step="scalarConfig(item.signalName, item.soaType).step"
                label
                color="primary"
                @update:model-value="
                  (val) => clampScalar(item.signalName, item.soaType, val)
                "
              />
            </div>
            <div class="col-12">
              <q-btn
                dense
                flat
                color="secondary"
                label="从车端获取"
                @click="handleGet(item.signalName, item.soaType)"
              />
            </div>
          </div>
        </div>
      </div>
    </q-card>
  </div>
</template>

<script setup lang="ts">
import { reactive } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import { useQuasar } from "quasar";
import type { SignalItem } from "@/composables/useCarProperty";
import {
  PREVIEWINFO_HU_CPS_ROWS,
  HU_CPS_SCALAR_DEFAULTS,
  isArraySoaType,
  parseArrayLength,
  isFloatSoaType,
  type PreviewHuCpsRow,
  type PreviewSoaType,
} from "@/services/domainControl/previewinfoSoaCatalog";

const router = useRouter();
const $q = useQuasar();
const goBack = () => router.back();

type PreviewRow = SignalItem & { zh: string; soaType: PreviewSoaType };

/** 本页暂不展示：预瞄目标物高度（SusObjHght）、长度/宽度（SusObjLengh） */
function isHiddenSusObjDimensionRow(row: PreviewHuCpsRow): boolean {
  const d = row.displayName;
  return d.includes("SusObjHght") || d.includes("SusObjLengh");
}

const HU_CPS_TOOL_ROWS = PREVIEWINFO_HU_CPS_ROWS.filter(
  (row) => !isHiddenSusObjDimensionRow(row)
);

const previewSignals: PreviewRow[] = HU_CPS_TOOL_ROWS.map((row) => ({
  signalName: row.signalName,
  displayName: row.displayName,
  zh: row.zh,
  soaType: row.type,
}));

/** 标量用 number；数组用 number[] */
const values = reactive<Record<string, number | number[]>>({});
/** 数组信号的 JSON 草稿 */
const arrayDraft = reactive<Record<string, string>>({});

function emptyArray(soaType: PreviewSoaType): number[] {
  const n = parseArrayLength(soaType);
  return Array.from({ length: n }, () => 0);
}

function initStores() {
  for (const row of HU_CPS_TOOL_ROWS) {
    const sn = row.signalName;
    if (isArraySoaType(row.type)) {
      const arr = emptyArray(row.type);
      values[sn] = arr;
      arrayDraft[sn] = JSON.stringify(arr);
    } else {
      values[sn] = HU_CPS_SCALAR_DEFAULTS[sn] ?? 0;
    }
  }
}

initStores();

const THROTTLE_MS = 400;
const lastSendTime: Record<string, number> = {};
const throttleTrailingTimers: Record<string, number | undefined> = {};

function scalarConfig(
  signalName: string,
  soaType: PreviewSoaType
): {
  min: number;
  max: number;
  step: number;
} {
  if (soaType === "uint8") {
    return { min: 0, max: 255, step: 1 };
  }
  if (soaType === "float") {
    if (signalName.includes("InsLatitude")) {
      return { min: -90, max: 90, step: 0.0000001 };
    }
    if (signalName.includes("InsLongitude")) {
      return { min: -180, max: 180, step: 0.0000001 };
    }
    if (signalName.includes("InsHeadingAngle")) {
      return { min: -180, max: 180, step: 0.01 };
    }
    if (signalName.includes("VehSpd")) {
      return { min: 0, max: 300, step: 0.1 };
    }
    if (signalName.includes("SusIMUAccVert")) {
      return { min: -50, max: 50, step: 0.01 };
    }
    return { min: -1e6, max: 1e6, step: 0.01 };
  }
  return { min: 0, max: 255, step: 1 };
}

function scalarValue(signalName: string): number {
  const v = values[signalName];
  return typeof v === "number" && !Number.isNaN(v) ? v : 0;
}

/** 列表区：说明该信号在 cps_tool 里应是 [] 还是 b"" */
function cpsArrayFormatTip(soaType: PreviewSoaType): string {
  if (isFloatSoaType(soaType)) {
    return "本页下发时会把 JSON 数组拼成 cps_tool 需要的 '[v0,v1,…]'（单引号包 JSON）；勿对 float[] 使用 b\"…\"。";
  }
  return "本页会把 JSON 数字数组转成 b\"十六进制\"，并以 'b\"…\"' 形式拼进 adb set 命令（由前端控制引号）。";
}

/** 输入框下方简短提示 */
function arrayInputHint(soaType: PreviewSoaType): string {
  if (isFloatSoaType(soaType)) {
    return "float[]：填 JSON；页面负责生成 set 命令中 = 右侧带单引号的数组字面量。";
  }
  return "uint8[]：填 JSON；页面负责转为 b\"hex\" 并加单引号后交给本地服务透传 adb。";
}

function formatCurrentDisplay(
  signalName: string,
  soaType: PreviewSoaType
): string {
  const v = values[signalName];
  if (isArraySoaType(soaType) && Array.isArray(v)) {
    return JSON.stringify(v);
  }
  if (typeof v === "number") return String(v);
  return "--";
}

function parseArrayJson(
  text: string,
  expectedLen: number,
  soaType: PreviewSoaType
): number[] | null {
  let arr: unknown;
  try {
    arr = JSON.parse(text.trim());
  } catch {
    return null;
  }
  if (!Array.isArray(arr) || arr.length !== expectedLen) {
    return null;
  }
  const float = isFloatSoaType(soaType);
  const out: number[] = [];
  for (let i = 0; i < arr.length; i++) {
    const x = arr[i];
    if (typeof x !== "number" || Number.isNaN(x)) {
      return null;
    }
    if (!float && (!Number.isInteger(x) || x < 0 || x > 255)) {
      return null;
    }
    out.push(x);
  }
  return out;
}

function scheduleSend(signalName: string, options?: { forceNotify?: boolean }) {
  const now = Date.now();
  const elapsed = now - (lastSendTime[signalName] ?? 0);

  const fire = () => {
    if (throttleTrailingTimers[signalName] !== undefined) {
      clearTimeout(throttleTrailingTimers[signalName]);
      throttleTrailingTimers[signalName] = undefined;
    }
    lastSendTime[signalName] = Date.now();
    void handleSend(signalName, options);
  };

  if (elapsed >= THROTTLE_MS) {
    fire();
  } else if (throttleTrailingTimers[signalName] === undefined) {
    throttleTrailingTimers[signalName] = window.setTimeout(() => {
      throttleTrailingTimers[signalName] = undefined;
      fire();
    }, THROTTLE_MS - elapsed);
  }
}

function clampScalar(
  signalName: string,
  soaType: PreviewSoaType,
  raw: number | null | undefined
) {
  const { min, max } = scalarConfig(signalName, soaType);
  const n = Number(raw ?? 0);
  if (Number.isNaN(n)) {
    values[signalName] = min;
  } else {
    values[signalName] = Math.min(max, Math.max(min, n));
  }
  scheduleSend(signalName);
}

function commitArrayAndMaybeSend(
  item: PreviewRow,
  options?: { forceNotify?: boolean }
) {
  const len = parseArrayLength(item.soaType);
  const parsed = parseArrayJson(arrayDraft[item.signalName], len, item.soaType);
  if (!parsed) {
    if (options?.forceNotify) {
      $q.notify({
        type: "warning",
        message: `无效 JSON：需长度为 ${len} 的数组，且类型与 ${item.soaType} 一致`,
        position: "top",
      });
    }
    return;
  }
  values[item.signalName] = parsed;
  scheduleSend(item.signalName, {
    forceNotify: options?.forceNotify ?? false,
  });
}

const resetAll = () => {
  for (const row of HU_CPS_TOOL_ROWS) {
    const sn = row.signalName;
    if (isArraySoaType(row.type)) {
      const arr = emptyArray(row.type);
      values[sn] = arr;
      arrayDraft[sn] = JSON.stringify(arr);
    } else {
      values[sn] = HU_CPS_SCALAR_DEFAULTS[sn] ?? 0;
    }
  }
};

/**
 * cps_tool set 中「= 右侧」字面量：标量用 number；float[] 为带单引号 JSON；uint8[] 为 'b"hex"'（服务只做字符串拼接）。
 */
function buildSetValueForCps(
  value: number | number[],
  soaType: PreviewSoaType | undefined
): number | string {
  if (!Array.isArray(value)) {
    return value;
  }
  if (!soaType || !isArraySoaType(soaType)) {
    return `'${JSON.stringify(value)}'`;
  }
  if (isFloatSoaType(soaType)) {
    return `'${JSON.stringify(value)}'`;
  }
  const hex = value
    .map((v) => {
      const n = Math.round(Number(v));
      return n.toString(16).padStart(2, "0");
    })
    .join("");
  return `'b"${hex}"'`;
}

function parseCpsValueRaw(raw: string): number | number[] | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("[")) {
    try {
      const arr = JSON.parse(trimmed) as unknown;
      if (!Array.isArray(arr) || arr.length === 0) return null;
      const nums: number[] = [];
      for (let i = 0; i < arr.length; i++) {
        const x = arr[i];
        if (typeof x !== "number" || Number.isNaN(x)) return null;
        nums.push(x);
      }
      return nums;
    } catch {
      return null;
    }
  }
  if (trimmed.startsWith("b\"") && trimmed.endsWith("\"")) {
    const inner = trimmed.slice(2, -1);
    if (!/^[0-9a-fA-F]*$/.test(inner) || inner.length % 2 !== 0) {
      return null;
    }
    const out: number[] = [];
    for (let i = 0; i < inner.length; i += 2) {
      out.push(parseInt(inner.slice(i, i + 2), 16));
    }
    return out;
  }
  const n = Number(trimmed);
  if (!Number.isNaN(n)) return n;
  return null;
}

const postSignal = async (
  signalName: string,
  value: number | number[],
  soaType?: PreviewSoaType
) => {
  const payload = buildSetValueForCps(value, soaType);
  await axios.post("http://localhost:3100/api/set-signal", {
    signalName,
    value: payload,
  });
};

const handleSend = async (
  signalName: string,
  options?: { forceNotify?: boolean }
) => {
  const value = values[signalName];
  const soaType = previewSignals.find((p) => p.signalName === signalName)
    ?.soaType;
  try {
    await postSignal(signalName, value as number | number[], soaType);
    if (options?.forceNotify) {
      $q.notify({
        type: "positive",
        message: `已下发 ${signalName}`,
        position: "top",
      });
    }
  } catch (e) {
    console.error(e);
    $q.notify({
      type: "negative",
      message: `下发失败：${signalName}`,
      position: "top",
    });
  }
};

const handleGet = async (signalName: string, soaType: PreviewSoaType) => {
  try {
    const resp = await axios.post("http://localhost:3100/api/get-signal", {
      signalName,
    });
    const raw =
      typeof resp.data?.valueRaw === "string"
        ? resp.data.valueRaw.trim()
        : "";
    const parsedFromRaw = raw ? parseCpsValueRaw(raw) : null;
    const fallbackNum = resp.data?.value;
    const v =
      parsedFromRaw ??
      (typeof fallbackNum === "number" && !Number.isNaN(fallbackNum)
        ? fallbackNum
        : null);
    const len = parseArrayLength(soaType);

    if (isArraySoaType(soaType)) {
      if (Array.isArray(v) && v.length === len) {
        const nums = v.map((x: unknown) => Number(x));
        if (nums.every((x) => !Number.isNaN(x))) {
          values[signalName] = nums;
          arrayDraft[signalName] = JSON.stringify(nums);
          $q.notify({
            type: "info",
            message: `已读取数组 ${signalName}`,
            position: "top",
          });
          return;
        }
      }
      $q.notify({
        type: "warning",
        message: `获取数组失败或长度不符：${signalName}（原始：${resp.data?.valueRaw ?? ""}）`,
        position: "top",
      });
      return;
    }

    if (typeof v === "number" && !Number.isNaN(v)) {
      values[signalName] = v;
      $q.notify({
        type: "info",
        message: `当前 ${signalName} = ${v}`,
        position: "top",
      });
    } else {
      $q.notify({
        type: "warning",
        message: `获取标量失败：${signalName}`,
        position: "top",
      });
    }
  } catch (e) {
    console.error(e);
    $q.notify({
      type: "negative",
      message: `获取失败：${signalName}`,
      position: "top",
    });
  }
};
</script>

<style scoped>
.signal-list {
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}
.text-break {
  word-break: break-all;
}
</style>
